/**
 * サーバーステータスダッシュボードページ
 *
 * システムリソース、Dockerコンテナ、Elasticsearch、リクエスト統計を
 * リアルタイムで監視するダッシュボード
 */

import React, { useEffect, useState, useCallback } from 'react';
import { Typography, Card, Row, Col, Spin, Alert, Button, Space } from 'antd';
import { ReloadOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { CONSOLE_THEME } from '@/shared/constants/colors';
import { GRID_CONFIG, RESPONSIVE_STYLES } from '@/shared/constants/breakpoints';
import { useResponsive } from '@/shared/hooks/useResponsive';
import { serverStatusApiService } from '@/features/server-status/services/serverStatusApiService';
import {
  ResourcePieChart,
  ContainerStatusTable,
} from '@/features/server-status/components';
import type { ServerStatusData, ResourceType } from '@/features/server-status/types/serverStatus.types';

const { Title, Text } = Typography;

// ===== 定数 =====

const DASHBOARD_CONSTANTS = {
  /** 自動更新間隔（ミリ秒） */
  AUTO_REFRESH_INTERVAL_MS: 30000,
  /** ローディング最小表示時間（ミリ秒） */
  MIN_LOADING_TIME_MS: 300,
} as const;

// ===== コンポーネント =====

export const DashboardPage: React.FC = () => {
  const { isMobile } = useResponsive();
  const [serverStatus, setServerStatus] = useState<ServerStatusData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const responsiveGutter = isMobile
    ? RESPONSIVE_STYLES.gutter.mobile
    : RESPONSIVE_STYLES.gutter.desktop;

  const resourceColProps = isMobile
    ? { xs: 12 }
    : GRID_CONFIG.statsCard.desktop;

  /**
   * サーバーステータスを取得
   */
  const fetchServerStatus = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await serverStatusApiService.getServerStatus();

      if (response.success) {
        setServerStatus(response.data);
        setLastUpdated(new Date());
      } else {
        setError('サーバーステータスの取得に失敗しました');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : '不明なエラーが発生しました';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  // 初回読み込みと自動更新
  useEffect(() => {
    fetchServerStatus();

    const intervalId = setInterval(
      fetchServerStatus,
      DASHBOARD_CONSTANTS.AUTO_REFRESH_INTERVAL_MS
    );

    return () => {
      clearInterval(intervalId);
    };
  }, [fetchServerStatus]);

  /**
   * リソースデータを生成
   */
  const getResourceData = (): Array<{ type: ResourceType; usedPercent: number; subLabel?: string }> => {
    if (serverStatus === null) {
      return [];
    }

    const MB_PER_GB = 1024;
    const system = serverStatus.system;
    const formatGB = (megabytes: number): string => (megabytes / MB_PER_GB).toFixed(1);
    return [
      {
        type: 'memory' as const,
        usedPercent: system.memoryUsedPercent,
        subLabel: `${formatGB(system.memoryUsedMB)} / ${formatGB(system.memoryTotalMB)} GB`,
      },
      { type: 'swap' as const, usedPercent: system.swapUsedPercent },
      { type: 'cpu' as const, usedPercent: system.cpuLoadAverage * 10 }, // 負荷平均を%表示に変換
      { type: 'disk' as const, usedPercent: system.diskUsedPercent },
    ];
  };

  /**
   * 最終更新時刻を表示
   */
  const formatLastUpdated = (): string => {
    if (lastUpdated === null) {
      return '未取得';
    }
    return lastUpdated.toLocaleTimeString('ja-JP');
  };

  // エラー表示
  if (error !== null && serverStatus === null) {
    return (
      <div>
        <Title level={2} style={{ color: CONSOLE_THEME.ACCENT }}>
          サーバーステータス
        </Title>
        <Alert
          message="データ取得エラー"
          description={error}
          type="error"
          showIcon
          action={
            <Button onClick={fetchServerStatus} icon={<ReloadOutlined />}>
              再試行
            </Button>
          }
        />
      </div>
    );
  }

  // ローディング表示（初回のみ）
  if (loading && serverStatus === null) {
    return (
      <div>
        <Title level={2} style={{ color: CONSOLE_THEME.ACCENT }}>
          サーバーステータス
        </Title>
        <div style={{ textAlign: 'center', padding: 40 }}>
          <Spin size="large" tip="読み込み中..." />
        </div>
      </div>
    );
  }

  const resourceData = getResourceData();

  return (
    <div>
      {/* ヘッダー */}
      <div className="mb-4" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
        <div>
          <Title level={2} style={{ color: CONSOLE_THEME.ACCENT, margin: 0 }}>
            サーバーステータス
          </Title>
          <Text type="secondary">システムリソースとサービス状態を監視</Text>
        </div>
        <Space>
          <Text type="secondary">
            <ClockCircleOutlined style={{ marginRight: 4 }} />
            最終更新: {formatLastUpdated()}
          </Text>
          <Button
            icon={<ReloadOutlined spin={loading} />}
            onClick={fetchServerStatus}
            loading={loading}
            size={isMobile ? 'small' : 'middle'}
          >
            更新
          </Button>
        </Space>
      </div>

      {/* エラー警告（データがある場合） */}
      {error !== null && serverStatus !== null && (
        <Alert
          message="更新エラー"
          description={error}
          type="warning"
          showIcon
          closable
          className="mb-4"
        />
      )}

      {/* システムリソース円グラフ */}
      <Card
        title={<span style={{ color: CONSOLE_THEME.ACCENT }}>システムリソース</span>}
        className="mb-4"
        size={isMobile ? 'small' : 'default'}
      >
        <Row gutter={responsiveGutter}>
          {resourceData.map((resource) => (
            <Col key={resource.type} {...resourceColProps}>
              <ResourcePieChart
                type={resource.type}
                usedPercent={resource.usedPercent}
                subLabel={resource.subLabel}
                isMobile={isMobile}
              />
            </Col>
          ))}
        </Row>
      </Card>

      {/* コンテナステータス */}
      <Card
        title={<span style={{ color: CONSOLE_THEME.ACCENT }}>Dockerコンテナ</span>}
        className="mb-4"
        size={isMobile ? 'small' : 'default'}
      >
        <ContainerStatusTable
          containers={serverStatus?.containers ?? []}
          isMobile={isMobile}
          loading={loading}
        />
      </Card>
    </div>
  );
};
